
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Pencil, Lock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { ServiceDetailsForm } from "./ServiceDetailsForm";
import { ServiceSettingsToggles } from "./ServiceSettingsToggles";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useRegionCurrency } from "@/hooks/useRegionCurrency";
import { useServicePermission } from "@/hooks/useServicePermission";

interface VendorServiceDetailsProps {
  service: any;
}

export const VendorServiceDetails = ({ service }: VendorServiceDetailsProps) => {
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: service.name || "",
    description: service.description || "",
    price: service.price?.toString() || "",
    additional_person: service.additional_person?.toString() || "",
    location: service.location || "",
    email: service.email || "",
    phone: service.phone || "",
    published: service.published || false,
    calendar: service.calendar || false,
    booking_notifications: service.booking_notifications !== false,
    review_notifications: service.review_notifications !== false,
    unit: service.unit || "Hour" // Add unit field with default
  });
  
  const { toast } = useToast();
  const navigate = useNavigate();
  const { data: regionCurrency } = useRegionCurrency(service.region || "nairobi");
  const { canEdit, loading: permissionLoading } = useServicePermission(service.id);

  const handleEditComplete = async () => {
    if (!editing) {
      setEditing(true);
      return;
    }

    if (!canEdit) {
      toast({
        title: "Permission Denied",
        description: "You don't have permission to edit this service",
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);
      
      const serviceData = {
        ...formData,
        price: parseFloat(formData.price) || null,
        additional_person: formData.additional_person ? parseFloat(formData.additional_person) : null,
      };

      const { error } = await supabase
        .from('services')
        .update(serviceData)
        .eq('id', service.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Service updated successfully",
        variant: "success",
      });
      
      setEditing(false);
      // Refresh the page to show updated data
      navigate(0);
    } catch (error) {
      console.error('Error saving service:', error);
      toast({
        title: "Error",
        description: "Failed to save service: " + (error as Error).message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (permissionLoading) {
    return <div className="p-4 text-center">Checking permissions...</div>;
  }

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium">Service Details</h3>
        {canEdit ? (
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleEditComplete}
            disabled={loading}
          >
            {editing ? "Save Changes" : (
              <>
                <Pencil className="h-4 w-4 mr-2" />
                Edit
              </>
            )}
          </Button>
        ) : (
          <div className="flex items-center text-gray-400 text-sm">
            <Lock className="h-4 w-4 mr-1" />
            Editing disabled
          </div>
        )}
      </div>
      
      {!canEdit && (
        <Alert className="mb-4 bg-amber-50 text-amber-800 border-amber-200">
          <AlertDescription>
            Editing is currently disabled for this service. Please contact the administrator to enable editing.
          </AlertDescription>
        </Alert>
      )}
      
      <div className="space-y-4">
        <ServiceDetailsForm
          formData={formData}
          setFormData={setFormData}
          editing={editing && canEdit}
          service={service}
          currency={regionCurrency}
        />
        
        <ServiceSettingsToggles
          formData={formData}
          setFormData={setFormData}
          editing={editing && canEdit}
          service={service}
        />
      </div>
    </div>
  );
};
